import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";
import { Navbar } from "../components/Navbar";
import { PostCard } from "../components/PostCard";
import { Avatar } from "../components/Avatar";
import { LockedCard } from "../components/Paywall";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { timeAgo } from "../lib/helpers";
import { toast } from "sonner";

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sub } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get(`/posts/${id}`),
        api.get(`/posts/${id}/comments`),
      ]);
      setPost(p.data);
      setComments(c.data);
    } catch {
      setNotFound(true);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/posts/${id}/comments`, { content: text });
      setComments((c) => [...c, res.data]);
      setText("");
      setPost((p) => ({ ...p, comment_count: (p.comment_count || 0) + 1 }));
    } catch {
      toast.error("Could not post your reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#6D7A6A] hover:text-[#2C362B] font-semibold mb-6 transition-colors" data-testid="back-btn">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>

        {notFound ? (
          <p className="text-center text-[#8A9A86] py-16">This post is no longer here.</p>
        ) : !post ? (
          <p className="text-center text-[#8A9A86] py-16">Loading…</p>
        ) : (
          <>
            <PostCard post={post} clickable={false} onChange={(d) => setPost(d)} onDeleted={() => navigate("/")} />

            <div className="mt-8">
              <h2 className="font-heading text-xl font-medium text-[#2C362B] mb-4">
                Supportive replies ({comments.length})
              </h2>

              {sub && !sub.access ? (
                <div className="mb-6">
                  <LockedCard title="Subscribe to reply" subtitle="Members can offer supportive replies. Reading stays free." />
                </div>
              ) : (
              <form onSubmit={submit} className="flex items-end gap-3 mb-6">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Offer some kind words…"
                  maxLength={2000}
                  rows={1}
                  className="flex-1 bg-white border border-[#E6EBE4] rounded-2xl px-5 py-3.5 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] resize-none outline-none transition-all min-h-[52px]"
                  data-testid="comment-input"
                />
                <button
                  type="submit"
                  disabled={sending || !text.trim()}
                  className="bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full p-3.5 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                  data-testid="comment-submit-btn"
                >
                  <PaperPlaneTilt size={20} weight="fill" />
                </button>
              </form>
              )}

              <div className="space-y-3" data-testid="comments-list">
                {comments.length === 0 ? (
                  <p className="text-center text-[#8A9A86] py-8">No replies yet. Be the first to offer support.</p>
                ) : (
                  comments.map((c) => (
                    <motion.div
                      key={c.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="bg-white rounded-2xl p-5 border border-[#E6EBE4]/60 flex gap-3"
                      data-testid="comment-item"
                    >
                      <Avatar handle={c.handle} size={36} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#414C3F] text-sm">{c.handle}</span>
                          <span className="text-xs text-[#A8B0A3]">{timeAgo(c.created_at)}</span>
                        </div>
                        <p className="text-[#515A4F] mt-1 whitespace-pre-wrap break-words">{c.content}</p>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
